import { onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { downloadWaylineFile } from '/@/api/wayline'
import {
  clearWaylinePreviewOnGMap,
  hasWaylinePreviewGeometry,
  renderWaylineModelOnGMap,
} from '/@/hooks/wayline-gmap-preview'
import { ELocalStorageKey, ERouterName } from '/@/types'
import { getRoot } from '/@/root'
import { useMyStore } from '/@/store'
import { parseWaylineKmzModel } from '/@/utils/parse-wayline-kmz'

/** 航线预览图层 id 前缀，清除时批量删掉 */
const PREFIX = '__wayline_prev__'

/** 在这些路由下根据 store 中的航线绘制 KMZ 预览（含任务计划里选航线） */
function isWaylinePreviewRoute (name: unknown): boolean {
  return (
    name === ERouterName.WAYLINE ||
    name === ERouterName.CREATE_PLAN ||
    name === ERouterName.SELECT_PLAN
  )
}

/**
 * 在航线库、创建计划等路由下，在共享高德地图上绘制当前 store 选中航线的 KMZ 预览。
 */
export function useWaylineMapPreview () {
  const store = useMyStore()
  const root = getRoot()
  const route = useRoute()
  let reqSeq = 0
  let retryTimer: ReturnType<typeof setTimeout> | undefined

  function clearPreview () {
    clearWaylinePreviewOnGMap(store, PREFIX)
  }

  function scheduleDraw () {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = undefined
    }
    const run = (attempt: number) => {
      const map = root.$map as any
      if (!map) {
        if (attempt < 40) {
          retryTimer = setTimeout(() => run(attempt + 1), 150)
        }
        return
      }
      drawPreview().catch(() => {})
    }
    run(0)
  }

  async function drawPreview () {
    const map = root.$map as any
    const AMap = root.$aMap as any
    if (!map || !AMap) return

    if (!isWaylinePreviewRoute(route.name)) {
      clearPreview()
      return
    }

    const info = store.state.waylineInfo as { id?: string; name?: string }
    const wid = localStorage.getItem(ELocalStorageKey.WorkspaceId)
    clearPreview()

    if (!info?.id || !wid) {
      return
    }

    const seq = ++reqSeq
    try {
      const blob = await downloadWaylineFile(wid, info.id) as Blob | undefined
      if (seq !== reqSeq) return
      if (!blob || !(blob instanceof Blob) || blob.size === 0) {
        return
      }
      const model = await parseWaylineKmzModel(blob)
      if (seq !== reqSeq) return
      if (!hasWaylinePreviewGeometry(model)) {
        return
      }

      const displayName = (model.routeName || info.name || '航线').trim()
      renderWaylineModelOnGMap(root, model, displayName, PREFIX)
    } catch (e) {
      console.warn('[航线地图预览]', e)
    }
  }

  watch(
    () => [route.name, (store.state.waylineInfo as { id?: string }).id] as const,
    () => scheduleDraw(),
    /** 进入航线库时 store 可能已有选中项，须立即尝试绘制，否则仅路由/组件挂载不会触发 watch */
    { flush: 'post', immediate: true }
  )

  onBeforeUnmount(() => {
    if (retryTimer) clearTimeout(retryTimer)
    clearPreview()
  })

  return { clearPreview }
}
